/**
 * Permissions Store - server-backed per-role permission overrides.
 * Source of truth: public.app_data row with data_key = 'khays_permissions_config'.
 * Only super_admin can read/write that row (enforced by RLS).
 *
 * We keep a synchronous in-memory cache so getPermissions() in useAuth.ts can
 * stay synchronous. The cache is hydrated by hydratePermissionConfig() once
 * the admin/manager session is established. Staff sessions cannot read the
 * config and will fall back to the DEFAULT_STAFF baseline (most-restrictive).
 *
 * Removed: localStorage as a source of truth. Previous client-side overrides
 * could be tampered with via DevTools to escalate UI-visible permissions.
 */
import { supabase } from "@/integrations/supabase/client";

const APP_DATA_KEY = "khays_permissions_config";
// Mirror the server config in localStorage purely as a same-tab cache between
// reloads to avoid a flash of restricted UI; it is overwritten by the server
// on every hydrate and is never trusted as the authoritative source.
const CACHE_KEY = "khays_permissions_cache_v2";

export interface PermissionConfig {
  manager: Record<string, boolean>;
  staff: Record<string, boolean>;
}

const DEFAULT_MANAGER: Record<string, boolean> = {
  canEditPricing: true,
  canEditStock: true,
  canChangeExchangeRate: false,
  canManageOrders: true,
  canManageTracking: false,
  canEditEnquiries: true,
  canGenerateInvoices: true,
  canViewAnalytics: true,
  canViewSalesAnalytics: true,
  canManageMedia: true,
  canManageDevices: true,
  canManageUsers: false,
  canDeleteUsers: false,
  canViewCommission: true,
  canManageSms: false,
  canCreateGroupChats: true,
  canChatWithUsers: false,
  canViewAllNotifications: true,
  canViewSyncStatus: false,
  canViewAllConversations: false,
  canManageAds: true,
  canManageTenants: false,
  canFilterByTenant: true,
  canReceiveLowStockEmails: true,
  canManageCatalog: false,
  canManageSponsorship: false,
  canManageSponsorshipAllowlist: false,

};

const DEFAULT_STAFF: Record<string, boolean> = {
  canEditPricing: false,
  canEditStock: false,
  canChangeExchangeRate: false,
  canManageOrders: true,
  canManageTracking: false,
  canEditEnquiries: false,
  canGenerateInvoices: true,
  canViewAnalytics: false,
  canViewSalesAnalytics: false,
  canManageMedia: false,
  canManageDevices: false,
  canManageUsers: false,
  canDeleteUsers: false,
  canViewCommission: true,
  canManageSms: false,
  canCreateGroupChats: false,
  canChatWithUsers: false,
  canViewAllNotifications: false,
  canViewSyncStatus: false,
  canViewAllConversations: false,
  canManageAds: false,
  canManageTenants: false,
  canFilterByTenant: false,
  canReceiveLowStockEmails: false,
  canManageCatalog: false,
  canManageSponsorship: false,
  canManageSponsorshipAllowlist: false,

};

export const PERMISSION_LABELS: Record<string, string> = {
  canEditPricing: "Edit Pricing",
  canEditStock: "Manage Stock",
  canChangeExchangeRate: "Change Exchange Rate",
  canManageOrders: "Manage Orders",
  canManageTracking: "Manage Tracking",
  canEditEnquiries: "Edit Enquiries",
  canGenerateInvoices: "Generate Invoices",
  canViewAnalytics: "View Visitor Analytics",
  canViewSalesAnalytics: "View Sales Analytics",
  canManageMedia: "Manage Media & Content",
  canManageDevices: "Manage Devices",
  canManageUsers: "Manage Users",
  canDeleteUsers: "Delete Users",
  canViewCommission: "View Commission",
  canManageSms: "Manage SMS/Notifications",
  canCreateGroupChats: "Create Group Chats",
  canChatWithUsers: "Chat With Other Users",
  canViewAllNotifications: "View All Notifications",
  canViewSyncStatus: "View Sync Status & Pending Changes",
  canViewAllConversations: "View All Conversations (Oversight)",
  canManageAds: "Manage Ads & Placements",
  canManageTenants: "Manage Store Tenants",
  canFilterByTenant: "Filter Admin Views by Tenant",
  canReceiveLowStockEmails: "Receive Low-Stock Email Alerts",
  canManageCatalog: "Manage Platform Category Catalog",
  canManageSponsorship: "Manage Sponsorship Program",
  canManageSponsorshipAllowlist: "Manage Sponsorship Opt-in Allowlist",

};

function defaults(): PermissionConfig {
  return { manager: { ...DEFAULT_MANAGER }, staff: { ...DEFAULT_STAFF } };
}

let cache: PermissionConfig = (() => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        manager: { ...DEFAULT_MANAGER, ...(parsed.manager || {}) },
        staff: { ...DEFAULT_STAFF, ...(parsed.staff || {}) },
      };
    }
  } catch {}
  return defaults();
})();

const listeners = new Set<() => void>();
function notify() { listeners.forEach((l) => { try { l(); } catch {} }); }

/** Synchronous read — cache only. Always seed cache via hydratePermissionConfig() after login. */
export function loadPermissionConfig(): PermissionConfig {
  return { manager: { ...cache.manager }, staff: { ...cache.staff } };
}

/** Subscribe to cache updates (e.g. after server hydrate). */
export function subscribePermissionConfig(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Fetch authoritative config from app_data. Only succeeds for super_admin/manager. */
export async function hydratePermissionConfig(): Promise<PermissionConfig> {
  try {
    const { data } = await supabase
      .from("app_data")
      .select("data_value")
      .eq("data_key", APP_DATA_KEY)
      .maybeSingle();
    if (data?.data_value) {
      const parsed = typeof data.data_value === "string"
        ? JSON.parse(data.data_value)
        : (data.data_value as any);
      cache = {
        manager: { ...DEFAULT_MANAGER, ...(parsed.manager || {}) },
        staff: { ...DEFAULT_STAFF, ...(parsed.staff || {}) },
      };
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch {}
      notify();
    } else {
      // One-time migration: if a legacy localStorage config exists, seed the server with it.
      try {
        const legacy = localStorage.getItem("khays_permissions_config");
        if (legacy) {
          const parsed = JSON.parse(legacy);
          const seed: PermissionConfig = {
            manager: { ...DEFAULT_MANAGER, ...(parsed.manager || {}) },
            staff: { ...DEFAULT_STAFF, ...(parsed.staff || {}) },
          };
          await supabase.from("app_data").upsert({
            data_key: APP_DATA_KEY,
            data_value: seed as any,
            updated_at: new Date().toISOString(),
          });
          cache = seed;
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch {}
          try { localStorage.removeItem("khays_permissions_config"); } catch {}
          notify();
        }
      } catch {}
    }
  } catch {
    // Non-admins are blocked by RLS — keep defaults.
  }
  return loadPermissionConfig();
}

/** Persist config to app_data. Only super_admin will succeed (RLS). */
export async function savePermissionConfig(config: PermissionConfig): Promise<{ error: Error | null }> {
  cache = {
    manager: { ...DEFAULT_MANAGER, ...config.manager },
    staff: { ...DEFAULT_STAFF, ...config.staff },
  };
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch {}
  notify();

  const { error } = await supabase.from("app_data").upsert({
    data_key: APP_DATA_KEY,
    data_value: cache as any,
    updated_at: new Date().toISOString(),
  });
  if (error) {
    console.error("savePermissionConfig:", error.message);
    return { error: new Error(error.message) };
  }
  return { error: null };
}

export function getCustomPermissions(role: "manager" | "staff"): Record<string, boolean> {
  return { ...cache[role] };
}
